import {
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverHeader, PopoverBody,
  VStack, HStack, Avatar, Button, Divider, Box, Icon, Link, Tooltip,
} from "@chakra-ui/react";
import CDS from "design-system";
import { Link as RouterLink } from "react-router";
import { FiCheck, FiExternalLink } from "react-icons/fi";
import { taskPathById } from "helpers/url/pages/v2/actionPaths";
import CaminoSkip from "components/Icon/CaminoSkip";
import RoleLabelTag from "components/Tag/UserTag/RoleLabelTag";
import { useTaskStatusMutation } from "../hooks";
import {
  statusLabel, isClosed as isStatusClosed, relativeDueLabel, absoluteDueLabel,
} from "../helpers";
import { deriveCellState } from "../helpers/cellState";
import { COCKPIT } from "../theme/cockpit";
import type { GridTask, GridUser } from "../types";

interface Props {
  task: GridTask;
  assignee: GridUser | undefined;
  fallbackRole?: string | null;
  children: React.ReactElement;
}

export default function TaskCellPopover({ task, assignee, fallbackRole = null, children }: Props) {
  const statusMutation = useTaskStatusMutation();

  const today = new Date();
  const startDate = task.startOn ? new Date(task.startOn) : null;
  const dueDate = task.dueAt ? new Date(task.dueAt) : task.dueOn ? new Date(task.dueOn) : null;
  const closed = isStatusClosed(task.status);
  // Upcoming tasks resolve to `not_started` in deriveCellState (never overdue),
  // so a simple kind check is all we need at the display layer.
  const showOverdueColor = deriveCellState(task, today).kind === "overdue";

  const onComplete = () => statusMutation.mutate({ taskId: task.id, status: "completed" });
  const onSkip = () => statusMutation.mutate({ taskId: task.id, status: "skipped" });
  const onReopen = () => statusMutation.mutate({ taskId: task.id, status: "active" });

  return (
    <Popover placement="bottom" isLazy>
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent w="320px">
        <PopoverArrow />
        <PopoverHeader>
          <Link
            as={RouterLink}
            to={taskPathById(task.id)}
            display="inline-flex"
            alignItems="center"
            gap={1}
            color="blue.600"
            _hover={{ textDecoration: "underline" }}
          >
            <CDS.Text12 fontWeight="semibold" noOfLines={2}>{task.name}</CDS.Text12>
            <Icon as={FiExternalLink} boxSize={3} />
          </Link>
        </PopoverHeader>
        <PopoverBody>
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <CDS.Text10 color="gray.500">Status</CDS.Text10>
              <CDS.Text12>{statusLabel(task.status)}</CDS.Text12>
            </HStack>
            {startDate && (
              <HStack justify="space-between">
                <CDS.Text10 color="gray.500">Starts</CDS.Text10>
                <CDS.Text12>{absoluteDueLabel(startDate)}</CDS.Text12>
              </HStack>
            )}
            {dueDate && (
              <HStack justify="space-between">
                <CDS.Text10 color="gray.500">Due</CDS.Text10>
                <Tooltip label={absoluteDueLabel(dueDate)} placement="top" hasArrow openDelay={300}>
                  <Box as="span">
                    <CDS.Text12
                      cursor="default"
                      color={showOverdueColor ? COCKPIT.red.mark : undefined}
                    >
                      {relativeDueLabel(dueDate, today)}
                    </CDS.Text12>
                  </Box>
                </Tooltip>
              </HStack>
            )}
            <HStack justify="space-between">
              <CDS.Text10 color="gray.500">Role</CDS.Text10>
              <RoleLabelTag role={task.assigneeRole ?? fallbackRole} />
            </HStack>
            <HStack justify="space-between">
              <CDS.Text10 color="gray.500">Assignee</CDS.Text10>
              {assignee ? (
                <HStack spacing={2}>
                  <Avatar size="2xs" name={assignee.fullName} src={assignee.avatarUrl ?? undefined} />
                  <CDS.Text12 noOfLines={1}>{assignee.fullName}</CDS.Text12>
                </HStack>
              ) : (
                <CDS.Text12 color="gray.500">Unassigned</CDS.Text12>
              )}
            </HStack>
            <Divider />
            <Box>
              {closed ? (
                <Button
                  size="sm"
                  variant="outline"
                  w="100%"
                  onClick={onReopen}
                  isLoading={statusMutation.isPending}
                >
                  Re-open
                </Button>
              ) : (
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant="outline"
                    flex={1}
                    leftIcon={<Icon as={CaminoSkip} boxSize={4} />}
                    onClick={onSkip}
                    isLoading={statusMutation.isPending}
                  >
                    Skip
                  </Button>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    flex={1}
                    leftIcon={<FiCheck />}
                    onClick={onComplete}
                    isLoading={statusMutation.isPending}
                  >
                    Complete
                  </Button>
                </HStack>
              )}
            </Box>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
